import { isNoConnection, statusOf } from '@lib/apiError'
import { getShortName } from '@/utils/userNames'
import type { GetUserType } from '@orbit/api-types'

/**
 * The copy around who is in a trip: the confirms the owner and a requester see
 * before acting, and the one line a failed action shows. Ported verbatim from
 * iOS `MembershipPresentation`. The copy is ours, never the server's, and a
 * member is named short ("Maya K."). Pure, so it is tested in node.
 */

/** A confirm, already worded: the action button names what it does. */
export interface MembershipConfirmation {
	title: string
	message: string
	confirmText: string
	cancelText: string
}

type Named = Pick<GetUserType, 'firstName' | 'lastName'>

export const removalConfirmation = (member: Named, tripName: string): MembershipConfirmation => {
	const name = getShortName(member)
	return {
		title: `Remove ${name}?`,
		message: `${name} will no longer be a member of '${tripName}'. What they added to the trip stays.`,
		confirmText: 'Remove',
		cancelText: 'Cancel',
	}
}

/** Withdrawing my pending Join request. A denied one is dismissed without asking. */
export const cancelRequestConfirmation = (tripName: string): MembershipConfirmation => ({
	title: 'Cancel request?',
	message: `Your request to join '${tripName}' will be withdrawn. You can ask again with the link or code.`,
	confirmText: 'Cancel request',
	cancelText: 'Keep it',
})

/** Turning Join approval off, which lets in everyone still waiting. */
export const stopApprovingConfirmation = (tripName: string): MembershipConfirmation => ({
	title: 'Stop approving new members?',
	message: `Everyone waiting to join '${tripName}' will be let in, and anyone with the link or code will join straight away.`,
	confirmText: 'Turn off',
	cancelText: 'Cancel',
})

/** The membership actions that can be refused. */
export type MembershipAction =
	| { kind: 'approveRequest' }
	| { kind: 'denyRequest' }
	/** Withdrawing my pending request, or clearing a denied one. */
	| { kind: 'cancelRequest' }
	/** `memberName` as the member is shown ("Maya K."). */
	| { kind: 'remove'; memberName: string }
	| { kind: 'updateApproval' }

/** How a failure opens: "Unable to remove the member". */
const UNABLE: Record<MembershipAction['kind'], string> = {
	approveRequest: 'Unable to approve the request',
	denyRequest: 'Unable to deny the request',
	cancelRequest: 'Unable to update your request',
	remove: 'Unable to remove the member',
	updateApproval: 'Unable to update approval',
}

/**
 * What a membership action that didn't take says, or null when there is nothing
 * to tell: a request being withdrawn was already gone (404), which is what was asked for.
 */
export const membershipFailure = (action: MembershipAction, error: unknown): string | null => {
	const status = statusOf(error)

	if (action.kind === 'cancelRequest' && status === 404) return null
	if ((action.kind === 'approveRequest' || action.kind === 'denyRequest') && status === 404) {
		// Cancelled by them, or answered on another device.
		return 'That request is no longer waiting'
	}
	if (action.kind === 'remove' && status === 441) {
		return `${action.memberName} is part of expenses or pay ups. Settle or remove them first.`
	}
	if (action.kind === 'updateApproval' && status === 403) {
		return 'Only the trip owner can change who needs approval to join'
	}
	if (isNoConnection(error)) return `${UNABLE[action.kind]}. Please check your internet and try again`
	return `${UNABLE[action.kind]}. Please try again later`
}
